/**
 * BarChartCard — Recharts BarChart wrapper styled for the dashboard.
 * Reusable for any "count/$ per category" view (Schedules per day, Aging
 * Report, future trend charts, etc.).
 *
 * Two coloring modes:
 *   - Single color: pass `barColor` (default slate-500). All bars same.
 *   - Per-bar colors: pass `data[].color` ("R,G,B" or CSS color). Each bar
 *     uses its own. Falls back to `barColor` when missing.
 *
 * Y-axis can format ticks as $ via `yFormat="currency"` (cents → "$X,XXX").
 */

import * as React from "react";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { formatCurrencyCents, formatCurrencyCompact, formatRgb } from "./types";

export interface BarChartCardData {
  label: string;
  value: number;
  /** Optional per-bar color ("R,G,B" triplet or any CSS color). */
  color?: string;
}

export interface BarChartCardProps {
  data: BarChartCardData[];
  loading?: boolean;
  /** Default bar color when data[].color is unset. Defaults to slate-500. */
  barColor?: string;
  /**
   * Y-axis label format. "number" (raw integer), "currency" (cents → $X,XXX),
   * or "currencyCompact" (cents → $1.4M / $250K) — pick compact for tight
   * widgets where full numbers won't fit.
   */
  yFormat?: "number" | "currency" | "currencyCompact";
  /** Optional Y-axis label. */
  yLabel?: string;
  /** Height in pixels. Defaults to 160. */
  height?: number;
  /**
   * Minimum bar pixel height for non-zero values, so a near-empty bucket
   * is still visible next to a giant one. Defaults to 0 (no minimum).
   * Note: Recharts skips zero bars entirely regardless.
   */
  minBarHeight?: number;
}

export function BarChartCard({
  data,
  loading,
  barColor = "#64748b",
  yFormat = "number",
  yLabel,
  height = 160,
  minBarHeight = 0,
}: BarChartCardProps) {
  if (loading) {
    return (
      <div
        className="w-full bg-gray-100 rounded animate-pulse"
        style={{ height }}
        aria-label="loading"
      />
    );
  }
  if (data.length === 0) {
    return <div className="text-xs text-gray-400">No data</div>;
  }

  const tickFormatter =
    yFormat === "currencyCompact"
      ? (v: number) => formatCurrencyCompact(v)
      : yFormat === "currency"
        ? (v: number) => formatCurrencyCents(v)
        : undefined;
  // Tooltip always shows the full currency value (compact is for axes only).
  const tooltipFormatter =
    yFormat === "currencyCompact" || yFormat === "currency"
      ? (v: number) => formatCurrencyCents(v)
      : (v: number) => v.toLocaleString("en-US");
  const yWidth = yFormat === "currency" ? 64 : yFormat === "currencyCompact" ? 44 : 32;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 4, right: 4, left: -4, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 10, fill: "#6b7280" }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 10, fill: "#6b7280" }}
          axisLine={false}
          tickLine={false}
          width={yWidth}
          allowDecimals={false}
          tickFormatter={tickFormatter}
          label={
            yLabel
              ? { value: yLabel, angle: -90, position: "insideLeft", style: { fontSize: 10 } }
              : undefined
          }
        />
        <Tooltip
          contentStyle={{
            fontSize: 12,
            border: "1px solid #e5e7eb",
            borderRadius: 4,
            padding: "4px 8px",
          }}
          labelStyle={{ fontWeight: 600 }}
          formatter={tooltipFormatter}
          cursor={{ fill: "rgba(0,0,0,0.04)" }}
        />
        <Bar dataKey="value" radius={[2, 2, 0, 0]} minPointSize={minBarHeight}>
          {data.map((entry, i) => (
            <Cell key={i} fill={entry.color ? formatRgb(entry.color, barColor) : barColor} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
